import React, { useMemo } from 'react';
import { getOrLoadExpandedSlap } from './Slaps';
import { Board, PlaceId, StickerDesignId, SidebarMode, ObjectiveId } from '../utils/Primitives';
import { formatTimeLong, formatDateLong } from '../utils/GeneralUtilities';
import AddressDisplay from '../components/AddressDisplay';
import { getStickerUrl } from '../stickers/StickerDesigns';
import { use } from 'react';
import { Address } from 'viem';
import { getObjective, getObjectives } from '../objectives/Objectives';
import { buildLocateUrl, type ShareImageFormat } from '../utils/shareLinks';

type SlapReviewerContentProps = {
  slapId: number | null;
  boardRef: React.MutableRefObject<Board>;
  onClose: () => void;
  handleReviewPlace: (placeId: PlaceId) => void;
  handleReviewPlaceOnly: (placeId: PlaceId) => void;
  handleReviewSticker: (stickerDesignId: StickerDesignId) => void;
  handleReviewPlayer: (playerAddress: Address) => void;
  setActiveSidebarMode?: (mode: SidebarMode) => void;
  setSidebarToObjectives: (objectiveId: ObjectiveId | null) => void;
};

export function SlapReviewerContent({
  slapId,
  boardRef,
  onClose,
  handleReviewPlace,
  handleReviewPlaceOnly,
  handleReviewSticker,
  handleReviewPlayer,
  setActiveSidebarMode,
  setSidebarToObjectives
}: SlapReviewerContentProps) {
  const promise = useMemo(() => {
    if (slapId !== null) {
      return getOrLoadExpandedSlap({ boardRef, slapId });
    }
    return Promise.resolve(null);
  }, [slapId, boardRef]);

  const slap = use(promise); // This works with Suspense

  // The generated share images are public, so surface them directly rather
  // than making anyone reconstruct the locate URLs by hand. buildLocateUrl
  // returns null when the chain id or CDN domain is not configured, in which
  // case the line is omitted entirely.
  const previewImageLinks = useMemo(() => {
    if (slapId === null) {
      return [];
    }
    const formats: ShareImageFormat[] = ['og', 'fc', 'sq'];
    return formats
      .map(format => ({
        format,
        url: buildLocateUrl({ resourceType: 'slap', resourceId: slapId, format }),
      }))
      .filter((link): link is { format: ShareImageFormat; url: string } =>
        link.url !== null
      );
  }, [slapId]);

  // Temporarily hidden objectives functionality
  /*
  // Get all available objectives from the board
  const allObjectives = useMemo(() => {
    const objectives = getObjectives({ boardRef });
    console.log('All available objectives:', objectives);
    return objectives;
  }, [boardRef]);

  const slapObjectives = useMemo(() => {
    if (!slap || !slap.objectiveIds) {
      console.log('No objectiveIds in slap:', slap);
      return [];
    }

    console.log('Slap objectiveIds:', slap.objectiveIds);

    return slap.objectiveIds
      .map(objectiveId => {
        const objective = getObjective({ boardRef, objectiveId: Number(objectiveId) });
        console.log(`Objective ${objectiveId}:`, objective);
        return objective;
      })
      .filter(Boolean); // Filter out null/undefined values
  }, [slap, boardRef]);

  console.log('Processed slapObjectives:', slapObjectives);
  */

  const handlePlaceClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (slap && slap.place) {
      handleReviewPlace(slap.place.id);
      onClose();
    }
  };

  const handleStickerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (slap && slap.sticker) {
      handleReviewSticker(slap.sticker.id);
      onClose();
    }
  };

  const handlePlayerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (slap && slap.player) {
      handleReviewPlayer(slap.player as Address);
      onClose();
    }
  };

  const handleObjectiveClick = (objectiveId: ObjectiveId) => {
    console.log(`Clicked on objective: ${objectiveId}`);

    if (!objectiveId) {
      console.error('No valid objectiveId provided');
      return;
    }

    setSidebarToObjectives(objectiveId);
    console.log(`Setting sidebar to objective: ${objectiveId}`);

    // Small delay to ensure console logs are visible
    setTimeout(() => {
      onClose();
    }, 100);
  };

  return (
    <div className="pt-4 grid grid-cols-2 gap-4">

      {/* Left Column: Cards */}
      <div className="flex flex-col space-y-4">
        {/* Place Card */}
        <div
          className="bg-gray-100 rounded-lg p-4 shadow-md cursor-pointer hover:scale-105 transition-transform"
          onClick={handlePlaceClick}
        >
          <p className="text-sm text-gray-600">Place: {slap?.place?.id}</p>
        </div>

        {/* Player Card */}
        <div
          className="bg-gray-100 rounded-lg p-4 shadow-md cursor-pointer hover:scale-105 transition-transform"
          onClick={handlePlayerClick}
        >
          <p className="text-sm text-gray-600">Slapped by: &nbsp;
            <AddressDisplay address={slap?.player ?? ''} ensName={slap?.playerEns ?? ''} />
          </p>
        </div>

        {/* Temporarily hidden objectives section */}
        {/*
        // Get all available objectives from the board
        const allObjectives = useMemo(() => {
          const objectives = getObjectives({ boardRef });
          console.log('All available objectives:', objectives);
          return objectives;
        }, [boardRef]);

        const slapObjectives = useMemo(() => {
          if (!slap || !slap.objectiveIds) {
            console.log('No objectiveIds in slap:', slap);
            return [];
          }

          console.log('Slap objectiveIds:', slap.objectiveIds);

          return slap.objectiveIds
            .map(objectiveId => {
              const objective = getObjective({ boardRef, objectiveId: Number(objectiveId) });
              console.log(`Objective ${objectiveId}:`, objective);
              return objective;
            })
            .filter(Boolean); // Filter out null/undefined values
        }, [slap, boardRef]);

        console.log('Processed slapObjectives:', slapObjectives);
        */}

        <div className="py-4 px-2">
          {previewImageLinks.length > 0 && (
            <p className="text-xs text-gray-600 mb-4">
              Preview:{' '}
              {previewImageLinks.map(({ format, url }, index) => (
                <React.Fragment key={format}>
                  {index > 0 && <span className="text-gray-400"> &middot; </span>}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-900 transition-colors"
                  >
                    {format}
                  </a>
                </React.Fragment>
              ))}
            </p>
          )}
          <p className="text-xs text-gray-600 mb-4">
            {formatDateLong(slap?.time ?? 0)} {formatTimeLong(slap?.time ?? 0)}
          </p>
          <p className="text-xs text-gray-600 mb-4">
            Size: {slap?.size ?? 'N/A'}
          </p>
        </div>
      </div>


      {/* Right Column: Slap Content */}
      <div className="flex flex-col space-y-4">
        {/* Sticker Card */}
        <div
          className="bg-gray-100 rounded-lg p-4 shadow-md"
        >
          <div 
            className="w-16 h-16 overflow-hidden rounded-lg shadow-lg 
              mx-auto flex items-center justify-center bg-transparent 
              cursor-pointer hover:scale-105 transition-transform"
            onClick={handleStickerClick}
          >
            {slap && slap.sticker && (
              <img
                src={getStickerUrl(slap.sticker)}
                alt={slap.sticker.name || 'Sticker'}
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
          <div className="text-center text-sm text-gray-600 font-bold pt-5 px-2 max-w-md break-words cursor-pointer 
              hover:text-gray-900 transition-colors" onClick={handleStickerClick}>
            {slap?.sticker?.name}
          </div>
          {slap?.sticker?.url && (
            <div className="text-xs text-gray-600 pt-3 max-w-[90%] mx-auto text-center">
              <a 
                href={slap.sticker.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline break-all"
              >
                {slap.sticker.url}
              </a>
            </div>
          )}

          <div 
            className="text-center text-xs text-gray-600 pt-4 pb-2
              px-2 max-w-md break-words"
          >
            Sticker #{slap?.sticker?.id}
          </div>

          <div className="text-center text-xs text-gray-600 max-w-md break-words pt-1">
            Publisher: &nbsp;
            <AddressDisplay
              address={slap?.sticker?.originalPublisher ?? ''}
              ensName={slap?.sticker?.originalPublisherEns ?? ''}
            />
          </div>
        </div>

      </div>



    </div>
  );
}

export function SlapReviewerLoading() {
  return (
    <div className="pt-4 grid grid-cols-2 gap-4">

      {/* Left Column: Cards */}
      <div className="flex flex-col space-y-4">
        {/* Place Card */}
        <div className="bg-gray-100 rounded-lg p-4 shadow-md cursor-pointer">
          <p className="text-sm text-gray-600 bg-gray-200 h-5 w-24" />
        </div>

        {/* Player Card */}
        <div className="bg-gray-100 rounded-lg p-4 shadow-md">
          <p className="text-sm text-gray-600 bg-gray-200 h-5 w-24" />
        </div>

        {/* Temporarily hidden objectives section */}
        {/*
        <div className="bg-gray-100 rounded-lg p-4 shadow-md">
          <p className="text-sm text-gray-600">Objectives:</p>
          <div className="text-xs text-gray-600">
            <span className="bg-gray-200 h-4 w-28 block" />
          </div>
        </div>
        */}

        <div className="py-4 px-2">
          <p className="text-sm text-gray-600 bg-gray-200 h-6 w-32" />
          <p className="text-sm text-gray-600 bg-gray-200 h-6 w-32" />
        </div>
      </div>


      {/* Right Column: Slap Content */}
      <div className="flex flex-col space-y-4">
        {/* Sticker Card */}
        <div className="bg-gray-100 rounded-lg p-4 shadow-md cursor-pointer hover:scale-105 transition-transform">
          <div className="w-16 h-16 overflow-hidden rounded-lg shadow-lg mx-auto bg-gray-200" />
          <div className="mt-2 text-center text-sm text-gray-600 p-2 max-w-md break-words bg-gray-200 h-10" />
          <div className="text-center text-sm text-gray-600 max-w-md break-words bg-gray-200 h-14" />
        </div>
      </div>



    </div>
  );
}
